// @ts-strict-ignore
import { Component, OnInit } from '@angular/core';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { CustomValidators } from '@shared/custom-validators';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CoverLabelStripArticleInsertProcessor } from '@project/components/article/processor/prepare/insert/cover-label-strip-article-insert.processor';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { BlockingZoneArticleManager } from '@project/components/article/manager/blocking-zone-article-manager';
import { BlockingZoneArticleManagerFactory } from '../manager/blocking-zone-article-manager.factory';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Component({
  standalone: false,
  templateUrl: './edit-blocking-zone.component.html',
  selector: 'app-edit-blocking-zone',

})
export class EditBlockingZoneComponent implements OnInit {
  form: FormGroup;

  articleUsage: ArticleUsageVO;

  private insertHelper: InsertArticleHelper;

  private articleLengthInUnits: number;
  private articleOffsetInUnits: number;

  readonly: boolean;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private mountingRailHelper: MountingRailHelper,
    private selectionModel: SelectionModel,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private containerService: ContainerService,
    private blockingZoneArticleManagerFactory: BlockingZoneArticleManagerFactory,
    private articleUsageService: ArticleUsageService,
    private validatorFactory: ValidatorFactory,
  ) {}

  ngOnInit(): void {
    this.insertHelper = this.mountingRailHelper.getInsertArticleHelper();
    const articleUsageId = this.selectionModel.getArticleUsageIds()[0];
    this.articleUsage = this.projectModel.getArticleUsage(articleUsageId);
    // const settingsArticle: ArticleVO = ArticleUsageUtils.get.getArticleByArticleUsage(articleUsage, this.articleManager);
    const articleContainer = this.containerService.enumerateArticleContainersInGlobal();
    const rail: ArticleVO = articleContainer.find((container) =>
      this.articleUsageService.isMountingRailArticleUsage(container.articleUsage)
    ).article;

    const maxDynamicArticleLength: number = ArticleUtils.getArticleBehaviorPropertyNumberValue(rail, ArticleBehaviorPropertyDef.MAX_LENGTH);

    const relocationValidator = (formGroup: FormGroup) => {
      return this.insertHelper.isRelocationAvailable(this.articleUsage.id, this.articleOffsetInUnits, this.articleLengthInUnits)
        ? null
        : { relocationValidError: true };
    };

    this.articleLengthInUnits = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      this.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_LENGTH
    );
    this.articleOffsetInUnits = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
      this.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_OFFSET
    );
    const articleLength: number = +UnitConverter.fromToRounded('3d', 'mm', this.articleLengthInUnits, 1);
    const articleOffset: number = +UnitConverter.fromToRounded('3d', 'mm', this.articleOffsetInUnits, 1);

    this.form = this.fb.group(
      {
        articleLength: [
          this.localeResourceManager.formatNumber(articleLength),
          [
            this.validatorFactory.number({min: CoverLabelStripArticleInsertProcessor.MIN_ARTICLE_LENGTH, max: maxDynamicArticleLength}),
            CustomValidators.trimRequired,
          ],
        ],
        articleOffset: [
          this.localeResourceManager.formatNumber(articleOffset),
          [
            this.validatorFactory.number({min: 0, max: maxDynamicArticleLength}),
            CustomValidators.trimRequired,
          ],
        ],
        isLeadingValue: [ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(this.articleUsage, ArticleUsageInfoPropertyDef.IS_LEADING_VALUE)],
      },
      {
        validator: relocationValidator,
      }
    );
    this.form.controls.articleLength.valueChanges.subscribe((value) => {
      this.articleLengthInUnits = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(value.toString()));
    });
    this.form.controls.articleOffset.valueChanges.subscribe((value) => {
      this.articleOffsetInUnits = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(value.toString()));
    });
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async saveButton_clickHandler(): Promise<void> {
    this.close();
    if (!this.articleUsageService.isBlockingZoneArticleUsage(this.articleUsage)) {
      return;
    }
    this.saveIsLeadingMeasurementValue(this.form.value.isLeadingValue);
    const blockingZoneArticleManager: BlockingZoneArticleManager = this.blockingZoneArticleManagerFactory.getNewInstance();
    await blockingZoneArticleManager.recreateArticle(this.articleUsage, this.articleLengthInUnits, this.articleOffsetInUnits);
  }

  private saveIsLeadingMeasurementValue(value: boolean) {
    this.projectModel.projectMetaDataChanged = true;
    ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(this.articleUsage, ArticleUsageInfoPropertyDef.IS_LEADING_VALUE, value);
  }
}
